import { estimateOffset, type ClockEstimate, type ClockSample } from '../game/clock';
import type { Message } from '../game/protocol';

export interface LinkMonitorOptions {
  /** Nombre d'échanges ping/pong conservés pour l'estimation d'horloge. */
  readonly maxSamples?: number;
}

export interface LinkMonitorResult {
  /** Réponse à envoyer (pong). */
  readonly reply?: Message;
  /** Nouvelle estimation d'horloge, si ce message l'a changée. */
  readonly clock?: ClockEstimate;
}

export interface LinkMonitor {
  onMessage(message: Message, receivedAt: number): LinkMonitorResult;
  makePing(now: number): Extract<Message, { type: 'ping' }>;
  heartbeatAge(now: number): number;
  clock(): ClockEstimate | null;
  pendingPings(): number;
}

/** Vivacité du lien (heartbeat) et synchronisation d'horloge (ping/pong). PUR. */
export function createLinkMonitor({ maxSamples = 10 }: LinkMonitorOptions = {}): LinkMonitor {
  let lastHeard = -Infinity;
  let nextId = 1;
  const pending = new Map<number, number>(); // id → t d'envoi
  let samples: ClockSample[] = [];
  let estimate: ClockEstimate | null = null;

  return {
    onMessage(message, receivedAt) {
      lastHeard = Math.max(lastHeard, receivedAt);
      if (message.type === 'ping') {
        return { reply: { type: 'pong', id: message.id, t: message.t, peerT: receivedAt } };
      }
      if (message.type !== 'pong') return {};
      const sentAt = pending.get(message.id);
      if (sentAt === undefined || sentAt !== message.t) return {};
      pending.delete(message.id);
      samples = [...samples, { sentAt, peerT: message.peerT, receivedAt }].slice(-maxSamples);
      estimate = estimateOffset(samples);
      return estimate ? { clock: estimate } : {};
    },
    makePing(now) {
      const id = nextId++;
      pending.set(id, now);
      // Pings perdus : on ne garde que les plus récents.
      for (const key of pending.keys()) {
        if (pending.size <= maxSamples) break;
        pending.delete(key);
      }
      return { type: 'ping', id, t: now };
    },
    heartbeatAge: (now) => now - lastHeard,
    clock: () => estimate,
    pendingPings: () => pending.size,
  };
}
